// Mark up one sheet of an RFI: draw clouds for a location, and place the
// exhibit stamp (owner, 2026-10-21: "add clouds and tags to the RFI editor").
//
// THE PICTURE IS ONLY A PICTURE; WHAT IS STORED IS SHEET COORDINATES. Every
// cloud and the stamp are kept in the sheet's own PDF user space - the space
// the package draws the sheet in - using the numbers the server sends with
// the picture (back/rfi/preview):
//   user x = box[0] + px / scale      user y = box[3] - py / scale
// The SVG on top of the picture works in picture pixels (its viewBox is the
// picture's size), so a pointer position is the same fraction of the SVG's
// on-screen box at any zoom, and the page's own `html { zoom }` drops out.
//
// THE TAG IS NOT DRAWN BY HAND. Each location's number is its place in the
// list, and the package puts its tag under the bottom-centre of its clouds,
// exactly where this view shows it (rfi/render/pages/sheetPage tagPoint).
//
// ARROWS AND CALLOUTS are the sheet's own (sheet.marks), drawn at the size
// the package prints them: the package scales them with the sheet (k = sheet
// width / tabloid width, rfi/render/pages/overlays), and so does this view.
// A callout's wrapping here is an estimate of Helvetica's; the PDF is exact.
// Each mark prints on the sheet, the key plan page or both (mark.on, absent =
// the sheet). On the key plan it is placed through the crop at page size
// (rfi/render/pages/keyPlanPage); here a key-plan-only mark is drawn faint
// and dashed, as it will not print on the sheet.
//
// ONE SHEET PER LOCATION: a location's clouds are on one sheet, so its tag
// has one place. Drawing for a location already marked on another sheet is
// refused with a message, not moved silently.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Input, Modal, Radio, Select, Space, Spin, Tag, Tooltip, Typography, message } from "antd";
import { DeleteOutlined, ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { fetchRfiSheetPreviewPng, getRfiSheetPreview } from "api/rfi/rfiApi";

const { Text } = Typography;
const RED = "#E0001B";
const BLUE = "#1677FF";
const MIN_DRAG_PX = 8;

const norm = ([a, b, c, d]) => [Math.min(a, c), Math.min(b, d), Math.max(a, c), Math.max(b, d)];

// The package's callout box (primitives/textBox): pad 8, gap 2, leading
// 1.25 x size, first line bold - all times k. Wrapping is estimated from
// Helvetica's average advance (0.52 em; bold 0.56).
function calloutLayout(text, width, k) {
  const size = 9 * k; const pad = 8 * k; const gap = 2 * k; const lead = size * 1.25;
  const inner = Math.max(1, width - pad * 2);
  const lines = [];
  String(text || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean).forEach((para, i) => {
    const per = Math.max(1, Math.floor(inner / (size * (i === 0 ? 0.56 : 0.52))));
    let cur = "";
    for (const word of para.split(/\s+/)) {
      if (cur && (cur.length + 1 + word.length) > per) { lines.push({ t: cur, bold: i === 0 }); cur = word; } else cur = cur ? `${cur} ${word}` : word;
    }
    lines.push({ t: cur, bold: i === 0, last: true });
  });
  const paras = lines.filter((l) => l.last).length;
  return { lines, size, pad, lead, height: pad * 2 + lines.length * lead + gap * Math.max(0, paras - 1), gap };
}
const nearestOnRect = ([x0, y0, x1, y1], [tx, ty]) => [Math.max(x0, Math.min(tx, x1)), Math.max(y0, Math.min(ty, y1))];

export default function SheetMarkup({ projectId, sheet, sheetIndex, c, set, disabled, onClose }) {
  // The sheet as it will print: without its old markups when they are removed.
  const bare = sheet.stripMarkups !== false;
  const [meta, setMeta] = useState(null);
  const [src, setSrc] = useState("");
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(0); // 0 = fit to width
  const [mode, setMode] = useState("cloud");
  const [locIndex, setLocIndex] = useState(() => {
    const k = c.locations.findIndex((l) => l.sheetDocId === sheet.docId);
    if (k >= 0) return k;
    const free = c.locations.findIndex((l) => !l.sheetDocId || !(l.clouds || []).length);
    return free >= 0 ? free : -1;
  });
  const [drag, setDrag] = useState(null);
  const [selected, setSelected] = useState(null); // { loc, idx }
  const [selMark, setSelMark] = useState(-1); // index into sheet.marks
  const [markOn, setMarkOn] = useState(sheet.keyPlan ? "both" : "sheet"); // where new marks print
  const svgRef = useRef(null);
  const boxRef = useRef(null);
  const [boxW, setBoxW] = useState(1000);

  useEffect(() => {
    let live = true;
    let made = "";
    setError("");
    Promise.all([getRfiSheetPreview(projectId, sheet.docId, sheet.page || 1, bare), fetchRfiSheetPreviewPng(projectId, sheet.docId, sheet.page || 1, bare)])
      .then(([r, blob]) => {
        if (!live) return;
        made = URL.createObjectURL(blob);
        setMeta(r.preview);
        setSrc(made);
      })
      .catch((e) => { if (live) setError(e.message || "Could not draw the sheet"); });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [projectId, sheet.docId, sheet.page, bare]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setBoxW(el.clientWidth || 1000));
    ro.observe(el);
    return () => ro.disconnect();
  }, [meta]);

  const toPx = ([x, y]) => [(x - meta.box[0]) * meta.scale, (meta.box[3] - y) * meta.scale];
  const toUser = ([px, py]) => [meta.box[0] + px / meta.scale, meta.box[3] - py / meta.scale];
  const pointer = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * meta.width, ((e.clientY - r.top) / r.height) * meta.height];
  };

  // Mirrors the package: tag radius from the sheet width, r + 8 pt below the
  // clouds' bottom-centre.
  const tagR = meta ? Math.max(10, Math.min(16, (meta.box[2] - meta.box[0]) / 220)) : 12;
  const here = useMemo(() => c.locations
    .map((l, k) => ({ l, k }))
    .filter(({ l }) => l.sheetDocId === sheet.docId && (l.clouds || []).length), [c.locations, sheet.docId]);

  const locOptions = [
    ...c.locations.map((l, k) => {
      const elsewhere = l.sheetDocId && l.sheetDocId !== sheet.docId && (l.clouds || []).length;
      return { value: k, label: `${k + 1}${l.grid ? ` - ${l.grid}` : ""}${elsewhere ? " (on another sheet)" : ""}` };
    }),
    { value: -1, label: "+ New location" },
  ];

  const setSheet = (p) => set({ sheets: c.sheets.map((s, j) => (j === sheetIndex ? { ...s, ...p } : s)) });
  const marks = sheet.marks || [];
  const sheetW = meta ? meta.box[2] - meta.box[0] : 1224;
  const k = Math.max(1, sheetW / 1224); // the package's scale for marks
  const r2 = (v) => Math.round(v * 100) / 100;
  const onOf = (m) => m.on || "sheet";
  const addMark = (m) => {
    if (marks.length >= 40) { message.warning("A sheet holds at most 40 arrows and callouts"); return; }
    setSheet({ marks: [...marks, sheet.keyPlan && markOn !== "sheet" ? { ...m, on: markOn } : m] });
    setSelMark(marks.length);
  };
  const kpHint = " On the key plan, arrows and callouts print at page size; the head (or the point a callout aims at) must be inside the key plan area. Faint dashed marks print on the key plan only.";
  const patchMark = (i, p) => setSheet({ marks: marks.map((m, j) => (j === i ? { ...m, ...p } : m)) });

  const addCloud = (rect) => {
    let k = locIndex;
    const locations = c.locations.map((l) => ({ ...l, clouds: [...(l.clouds || [])] }));
    if (k === -1) {
      if (locations.length >= 30) { message.warning("An RFI holds at most 30 locations"); return; }
      locations.push({ grid: "", room: "", clash: "", proposed: "", imageIds: [], sheetDocId: sheet.docId, clouds: [] });
      k = locations.length - 1;
      setLocIndex(k);
    }
    const l = locations[k];
    if (l.sheetDocId && l.sheetDocId !== sheet.docId && l.clouds.length) {
      message.warning(`Location ${k + 1} is already marked on another sheet - a location's clouds stay on one sheet. Pick another location or a new one.`);
      return;
    }
    if (l.clouds.length >= 20) { message.warning("A location holds at most 20 clouds"); return; }
    l.sheetDocId = sheet.docId;
    l.clouds.push(rect.map((v) => Math.round(v * 100) / 100));
    set({ locations });
  };

  const removeSelected = () => {
    if (selMark >= 0) { setSheet({ marks: marks.filter((_, j) => j !== selMark) }); setSelMark(-1); return; }
    if (!selected) return;
    const locations = c.locations.map((l, k) => (k !== selected.loc ? l : {
      ...l,
      clouds: l.clouds.filter((_, j) => j !== selected.idx),
    })).map((l) => (l.clouds && !l.clouds.length ? { ...l, sheetDocId: l.sheetDocId === sheet.docId ? "" : l.sheetDocId } : l));
    set({ locations });
    setSelected(null);
  };

  useEffect(() => {
    const onKey = (e) => {
      // Not while typing a callout's text.
      if (e.target && /^(TEXTAREA|INPUT)$/.test(e.target.tagName)) return;
      if ((e.key === "Delete" || e.key === "Backspace") && (selected || selMark >= 0) && !disabled) { e.preventDefault(); removeSelected(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const onDown = (e) => {
    if (disabled || !meta || e.button !== 0) return;
    const p = pointer(e);
    if (mode === "stamp") {
      const [x, y] = toUser(p);
      set({ sheets: c.sheets.map((s, j) => (j === sheetIndex ? { ...s, stampAt: [Math.round(x * 100) / 100, Math.round(y * 100) / 100] } : s)) });
      return;
    }
    setSelected(null);
    setSelMark(-1);
    setDrag({ a: p, b: p });
  };
  const onMove = (e) => { if (drag) setDrag({ ...drag, b: pointer(e) }); };
  const onUp = () => {
    if (!drag) return;
    const moved = Math.hypot(drag.b[0] - drag.a[0], drag.b[1] - drag.a[1]) >= MIN_DRAG_PX;
    if (mode === "arrow") {
      setDrag(null);
      // Tail where the drag starts, head where it ends.
      if (moved) addMark({ kind: "arrow", from: toUser(drag.a).map(r2), to: toUser(drag.b).map(r2) });
      return;
    }
    if (mode === "callout") {
      setDrag(null);
      // Drag from the thing pointed at to where the box goes; a click is a
      // box without a leader. The box's top-left is where the button is let go.
      addMark({
        kind: "callout", at: toUser(drag.b).map(r2), width: r2(Math.min(sheetW * 0.18, 150 * k + 60)), text: "Note",
        ...(moved ? { to: toUser(drag.a).map(r2) } : {}),
      });
      return;
    }
    const [x0, y0, x1, y1] = norm([...drag.a, ...drag.b]);
    setDrag(null);
    if (x1 - x0 < MIN_DRAG_PX || y1 - y0 < MIN_DRAG_PX) return;
    const a = toUser([x0, y1]);
    const b = toUser([x1, y0]);
    const r = norm([a[0], a[1], b[0], b[1]]);
    if (mode === "keyPlan") {
      // Drawing the area is asking for the page, so it turns the page on.
      setSheet({ keyPlan: true, keyPlanRect: r.map((v) => Math.round(v * 100) / 100) });
      return;
    }
    addCloud(r);
  };

  const fitZoom = meta ? Math.min(1, (boxW - 4) / meta.width) : 1;
  const z = zoom || fitZoom;
  const stamp = sheet.stampAt && meta ? toPx(sheet.stampAt) : null;
  const area = sheet.keyPlanRect && meta
    ? (() => { const r = sheet.keyPlanRect; const a = toPx([r[0], r[3]]); const b = toPx([r[2], r[1]]); return [a[0], a[1], b[0], b[1]]; })()
    : null;
  const stroke = Math.max(2, 3 / z);

  return (
    <Modal
      open width="calc(96 * var(--vw))" style={{ top: 16 }} title={`Mark up - ${[sheet.sheetNo, sheet.name].filter(Boolean).join(" - ") || "sheet"}`}
      onCancel={onClose} footer={<Button type="primary" onClick={onClose}>Done</Button>} destroyOnClose
    >
      {error && <Alert type="error" showIcon message={error} />}
      {!error && !meta && <Spin />}
      {meta && meta.rotation ? (
        <Alert type="warning" showIcon message={`This page is rotated ${meta.rotation} degrees, so clouds cannot be placed on it reliably here. Use an unrotated copy of the sheet.`} />
      ) : null}
      {meta && !meta.rotation && (
        <>
          <Space wrap style={{ marginBottom: 8 }}>
            <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} disabled={disabled} optionType="button" buttonStyle="solid"
                         options={[
                           { value: "cloud", label: "Draw clouds" }, { value: "arrow", label: "Arrow" }, { value: "callout", label: "Callout" },
                           { value: "stamp", label: "Place stamp" }, { value: "keyPlan", label: "Key plan area" },
                         ]} />
            {mode === "cloud" && (
              <>
                <Text>for location</Text>
                <Select style={{ minWidth: 220 }} value={locIndex} options={locOptions} onChange={setLocIndex} disabled={disabled} />
              </>
            )}
            {mode === "stamp" && sheet.stampAt && !disabled && (
              <Button size="small" onClick={() => set({ sheets: c.sheets.map((s, j) => (j === sheetIndex ? { ...s, stampAt: undefined } : s)) })}>
                Back to top-left
              </Button>
            )}
            {mode === "callout" && selMark >= 0 && marks[selMark] && marks[selMark].kind === "callout" && !disabled && (
              <Input.TextArea
                autoFocus autoSize={{ minRows: 1, maxRows: 4 }} style={{ width: 320 }} maxLength={600}
                placeholder="First line is the heading" value={marks[selMark].text}
                onChange={(e) => patchMark(selMark, { text: e.target.value })}
              />
            )}
            {(mode === "arrow" || mode === "callout") && (sheet.keyPlan || (selMark >= 0 && marks[selMark] && marks[selMark].on)) && (
              <>
                <Text>Show on</Text>
                <Radio.Group
                  size="small" optionType="button" disabled={disabled}
                  value={selMark >= 0 && marks[selMark] ? onOf(marks[selMark]) : markOn}
                  onChange={(e) => {
                    const v = e.target.value;
                    setMarkOn(v);
                    if (selMark >= 0 && marks[selMark]) patchMark(selMark, { on: v === "sheet" ? undefined : v });
                  }}
                  options={[{ value: "sheet", label: "Sheet" }, { value: "keyPlan", label: "Key plan" }, { value: "both", label: "Both" }]}
                />
              </>
            )}
            {mode === "keyPlan" && sheet.keyPlanRect && !disabled && (
              <Button size="small" onClick={() => setSheet({ keyPlanRect: undefined })}>Frame around the clouds</Button>
            )}
            <Tooltip title="Delete the selected cloud, arrow or callout (or press Delete)">
              <Button icon={<DeleteOutlined />} disabled={(!selected && selMark < 0) || disabled} onClick={removeSelected} />
            </Tooltip>
            <Button icon={<ZoomOutOutlined />} onClick={() => setZoom(Math.max(fitZoom, z / 1.5))} />
            <Button onClick={() => setZoom(0)}>Fit</Button>
            <Button icon={<ZoomInOutlined />} onClick={() => setZoom(Math.min(3, z * 1.5))} />
          </Space>
          <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            {mode === "cloud"
              ? "Drag a box around the problem. Each location gets a red numbered tag under its clouds; click a cloud to select it."
              : mode === "arrow"
                ? `Drag from where the arrow starts to what it points at. Click an arrow to select it.${sheet.keyPlan ? kpHint : ""}`
              : mode === "callout"
                ? `Drag from the thing to point at to where the note box goes (or click for a box without a leader), then type the note. The first line prints bold.${sheet.keyPlan ? kpHint : ""}`
              : mode === "stamp"
                ? "Click where the top-left corner of the red exhibit stamp should go - somewhere clear of the drawing."
                : "Drag the area the key plan page should show enlarged. Drawing it adds the key plan page; without an area the page frames the clouds."}
          </Text>
          <div ref={boxRef} style={{ overflow: "auto", maxHeight: "calc(70 * var(--vh))", border: "1px solid #eee", background: "#fafafa" }}>
            <div style={{ position: "relative", width: meta.width * z, height: meta.height * z }}>
              <img alt="" src={src} draggable={false} style={{ width: "100%", height: "100%", display: "block", userSelect: "none" }} />
              <svg
                ref={svgRef} viewBox={`0 0 ${meta.width} ${meta.height}`} data-testid="sheet-markup"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", cursor: disabled ? "default" : "crosshair" }}
                onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
              >
                {here.map(({ l, k }) => {
                  const px = l.clouds.map((r) => { const a = toPx([r[0], r[3]]); const b = toPx([r[2], r[1]]); return [a[0], a[1], b[0], b[1]]; });
                  const u = norm([Math.min(...l.clouds.map((r) => r[0])), Math.min(...l.clouds.map((r) => r[1])), Math.max(...l.clouds.map((r) => r[2])), Math.max(...l.clouds.map((r) => r[3]))]);
                  const [tx, ty] = toPx([(u[0] + u[2]) / 2, u[1] - (tagR + 8)]);
                  const tr = tagR * meta.scale;
                  return (
                    <g key={k}>
                      {px.map((r, j) => {
                        const sel = selected && selected.loc === k && selected.idx === j;
                        return (
                          <rect
                            // eslint-disable-next-line react/no-array-index-key
                            key={j} x={r[0]} y={r[1]} width={r[2] - r[0]} height={r[3] - r[1]} rx={8 / z}
                            fill={sel ? "rgba(224,0,27,0.12)" : "rgba(224,0,27,0.03)"} stroke={RED} strokeWidth={sel ? stroke * 2 : stroke}
                            strokeDasharray={`${10 / z} ${4 / z}`} style={{ cursor: "pointer" }}
                            onMouseDown={(e) => { if (mode !== "cloud") return; e.stopPropagation(); setSelected({ loc: k, idx: j }); setLocIndex(k); }}
                          />
                        );
                      })}
                      <circle cx={tx} cy={ty} r={tr} fill={RED} stroke="#fff" strokeWidth={Math.max(1, tr / 8)} />
                      <text x={tx} y={ty} fill="#fff" fontSize={tr * 1.15} fontWeight="700" textAnchor="middle" dominantBaseline="central" style={{ pointerEvents: "none" }}>{k + 1}</text>
                    </g>
                  );
                })}
                {marks.map((m, i) => {
                  const sel = i === selMark;
                  const col = RED;
                  const sc = meta.scale;
                  const kpOnly = onOf(m) === "keyPlan";
                  const look = kpOnly ? { opacity: 0.45 } : {};
                  const dash = kpOnly ? `${8 * k * sc} ${5 * k * sc}` : undefined;
                  const pick = (e) => { if (mode !== "arrow" && mode !== "callout") return; e.stopPropagation(); setSelMark(i); setSelected(null); setMode(m.kind); };
                  const head = (from, to, size) => {
                    const ang = Math.atan2(to[1] - from[1], to[0] - from[0]);
                    const back = (a) => [to[0] - size * Math.cos(ang + a), to[1] - size * Math.sin(ang + a)];
                    const l = back(0.4); const r = back(-0.4);
                    return { pts: `${to[0]},${to[1]} ${l[0]},${l[1]} ${r[0]},${r[1]}`, base: [(l[0] + r[0]) / 2, (l[1] + r[1]) / 2] };
                  };
                  if (m.kind === "arrow") {
                    const a = toPx(m.from); const b = toPx(m.to);
                    const h = head(a, b, 9 * k * sc);
                    const sw = 1.5 * k * sc * (sel ? 1.8 : 1);
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <g key={`m${i}`} style={{ cursor: "pointer", ...look }} onMouseDown={pick}>
                        <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke="transparent" strokeWidth={Math.max(sw * 4, 12 / z)} />
                        <line x1={a[0]} y1={a[1]} x2={h.base[0]} y2={h.base[1]} stroke={col} strokeWidth={sw} strokeDasharray={dash} />
                        <polygon points={h.pts} fill={col} />
                      </g>
                    );
                  }
                  const lay = calloutLayout(m.text, m.width, k);
                  const [bx, by] = toPx(m.at);
                  const rect = [m.at[0], m.at[1] - lay.height, m.at[0] + m.width, m.at[1]];
                  let leader = null;
                  if (m.to) {
                    const from = nearestOnRect(rect, m.to);
                    if (Math.hypot(m.to[0] - from[0], m.to[1] - from[1]) > 4 * k) {
                      const a = toPx(from); const b = toPx(m.to);
                      const h = head(a, b, 9 * k * sc);
                      leader = (
                        <>
                          <line x1={a[0]} y1={a[1]} x2={h.base[0]} y2={h.base[1]} stroke={col} strokeWidth={1.5 * k * sc} />
                          <polygon points={h.pts} fill={col} />
                        </>
                      );
                    }
                  }
                  let ty = by + lay.pad * sc;
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <g key={`m${i}`} style={{ cursor: "pointer", ...look }} onMouseDown={pick}>
                      {leader}
                      <rect x={bx} y={by} width={m.width * sc} height={lay.height * sc} fill="#fff" stroke={col} strokeWidth={1.2 * k * sc * (sel ? 2 : 1)} strokeDasharray={dash} />
                      {lay.lines.map((l, j) => {
                        const y = ty + lay.size * 0.8 * sc;
                        ty += lay.lead * sc + (l.last ? lay.gap * sc : 0);
                        // eslint-disable-next-line react/no-array-index-key
                        return <text key={j} x={bx + lay.pad * sc} y={y} fontSize={lay.size * sc} fontWeight={l.bold ? 700 : 400} fontFamily="Helvetica, Arial, sans-serif" fill="#222" style={{ pointerEvents: "none" }}>{l.t}</text>;
                      })}
                    </g>
                  );
                })}
                {area && (
                  <g style={{ pointerEvents: "none" }}>
                    <rect x={area[0]} y={area[1]} width={area[2] - area[0]} height={area[3] - area[1]} fill="rgba(22,119,255,0.04)" stroke={BLUE} strokeWidth={stroke} strokeDasharray={`${14 / z} ${6 / z}`} />
                    <text x={area[0] + 8 / z} y={area[1] + 22 / z} fill={BLUE} fontSize={16 / z} fontWeight="700">Key plan area</text>
                  </g>
                )}
                {drag && (() => {
                  const [x0, y0, x1, y1] = norm([...drag.a, ...drag.b]);
                  const col = mode === "keyPlan" ? BLUE : RED;
                  if (mode === "arrow" || mode === "callout") {
                    return <line x1={drag.a[0]} y1={drag.a[1]} x2={drag.b[0]} y2={drag.b[1]} stroke={RED} strokeWidth={stroke} strokeDasharray={`${6 / z} ${4 / z}`} />;
                  }
                  return <rect x={x0} y={y0} width={x1 - x0} height={y1 - y0} fill="none" stroke={col} strokeWidth={stroke} strokeDasharray={`${6 / z} ${4 / z}`} />;
                })()}
                {stamp && (
                  <g style={{ pointerEvents: "none" }}>
                    <path d={`M${stamp[0]},${stamp[1] + 60 / z} L${stamp[0]},${stamp[1]} L${stamp[0] + 60 / z},${stamp[1]}`} stroke={RED} strokeWidth={stroke * 1.5} fill="none" />
                    <text x={stamp[0] + 8 / z} y={stamp[1] + 22 / z} fill={RED} fontSize={16 / z} fontWeight="700">Exhibit stamp</text>
                  </g>
                )}
              </svg>
            </div>
          </div>
          <Space wrap style={{ marginTop: 8 }}>
            {here.length
              ? here.map(({ l, k }) => <Tag color="red" key={k}>{k + 1}: {l.clouds.length} cloud{l.clouds.length === 1 ? "" : "s"}</Tag>)
              : <Text type="secondary">No clouds on this sheet yet.</Text>}
            {marks.length > 0 && <Tag>{marks.filter((m) => m.kind === "arrow").length} arrow(s), {marks.filter((m) => m.kind === "callout").length} callout(s)</Tag>}
            {marks.some((m) => onOf(m) !== "sheet") && (
              <Tag color={sheet.keyPlan ? "blue" : "orange"}>
                {marks.filter((m) => onOf(m) !== "sheet").length} on the key plan{sheet.keyPlan ? "" : " - the key plan page is off"}
              </Tag>
            )}
            {!sheet.stampAt && <Text type="secondary">Stamp: top-left corner until placed.</Text>}
          </Space>
        </>
      )}
    </Modal>
  );
}
